// Shared contract for the `compose_email` client-side agent tool.
//
// The schema is defined by the chat server (crm-chat-api) so the model knows
// the tool, but the tool is EXECUTED in the CRM browser (no server execute):
// the CRM renders its email composer pre-filled with the draft and the user
// sends it themselves. This module is the single source of truth for the
// input/output shape so the server tool definition and the CRM handler can't
// drift. `common-types` is a types-only package, so each side keeps its own
// runtime validator (zod) and constrains it to these types.

export const COMPOSE_EMAIL_TOOL = 'compose_email';

export const COMPOSE_EMAIL_LIMITS = {
  maxRecipients: 50, // per list (to, cc, bcc each), single delivery
  maxIndividualRecipients: 100, // `to`, individual delivery
  maxFiles: 10,
  maxSubject: 500,
  maxBody: 50_000, // characters of bodyHtml
  maxParents: 10,
} as const;

export const COMPOSE_EMAIL_PARENT_TYPES = [
  'lead',
  'buyer_lead',
  'enquiry',
  'lot',
  'contact',
] as const;
export type ComposeEmailParentType =
  (typeof COMPOSE_EMAIL_PARENT_TYPES)[number];

export const COMPOSE_EMAIL_DELIVERIES = ['single', 'individual'] as const;
export type ComposeEmailDelivery = (typeof COMPOSE_EMAIL_DELIVERIES)[number];

export type ComposeEmailRecipient = {
  email: string;
  name?: string;
  /** users.id (uuid) of the CRM contact/user behind this address, when known. */
  userId?: string;
  /** buyer_leads.id; individual delivery only. Links that recipient's email to the buyer lead. */
  buyerLeadId?: string;
};

export type ComposeEmailParent = {
  type: ComposeEmailParentType;
  id: string; // uuid
};

export type ComposeEmailInput = {
  /** 'single' (default): one email to all recipients. 'individual': one email per `to` recipient, sent as a campaign. */
  delivery?: ComposeEmailDelivery;
  to: ComposeEmailRecipient[]; // min 1
  cc?: ComposeEmailRecipient[]; // single only
  bcc?: ComposeEmailRecipient[]; // single only
  subject?: string; // required unless templateId or replyToEmailMessageId
  bodyHtml?: string; // restricted HTML; mutually exclusive with templateId
  templateId?: string; // email_templates.id
  /** Min 1 when single (the composer refuses to send without a parent); optional when individual. */
  parents?: ComposeEmailParent[];
  replyToEmailMessageId?: string; // single only; email_messages.id (uuid) of the message being replied to
  fileIds?: string[]; // files.id (uuid) already stored in the CRM
};

export type ComposeEmailOutput =
  | {
      status: 'sent';
      emailMessageId: string | null; // from the send response `emailMessage.id`
      /** Individual delivery: `campaignId` from the campaign endpoint. Absent in older stored outputs (parse as null). */
      campaignId: string | null;
      /** Absent in older stored outputs (parse as `to.length`). */
      recipientCount: number;
      subject: string; // final subject as sent
      to: string[]; // final To addresses as sent
      /** Whether the user changed the drafted subject/body; null when it cannot be determined (template/reply drafts). */
      edited: boolean | null;
    }
  | { status: 'discarded' }
  | { status: 'error'; error: string }; // raw error text, never a generic message
